import React from "react";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";

/**
 * WeekStrip — the seven-column mini chart used by both week sections.
 *
 * WHY A BAR CHART AND NOT A TABLE
 * "Which day is empty?" is the question this answers, and that is a shape
 * question, not a number question. A table of seven rows makes you compare
 * numbers; a strip shows the hole instantly.
 *
 * `days` shape: [{ date, weekday, ...metrics }]. `series` describes which keys
 * to stack, so one component serves "booked vs delivered" (this week) and
 * "interpretation / translation / other" (next week) without a second chart
 * implementation.
 */
const WeekStrip = ({
  days,
  series,
  height = 64,
  showValues = true,
  emptyHint = "Nothing booked",
}) => {
  const theme = useTheme();
  const max = Math.max(
    1,
    ...days.map((day) =>
      series.reduce((total, entry) => total + Number(day[entry.key] || 0), 0),
    ),
  );

  return (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-end", width: "100%" }}>
      {days.map((day) => {
        const total = series.reduce(
          (sum, entry) => sum + Number(day[entry.key] || 0),
          0,
        );
        const barHeight = total === 0 ? 3 : Math.max(6, (total / max) * height);
        const isDim = day.isFuture;

        return (
          <Tooltip
            key={day.date}
            arrow
            // 2026-11-03 (Amine): this tooltip used MUI's default surface —
            // rgba(97,97,97,.92), a hardcoded dark grey — so in light mode it
            // read as a foreign dark box with white text, exactly like the
            // chart tooltips did before they were mapped. Every tooltip surface
            // now comes from the portal palette so it follows the theme in both
            // modes instead of being hardcoded either way.
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "12px",
                  boxShadow: theme.shadows[2],
                  px: 1.25,
                  py: 0.75,
                },
              },
              arrow: {
                sx: {
                  color: theme.palette.background.paper,
                  "&::before": {
                    border: `1px solid ${theme.palette.divider}`,
                  },
                },
              },
            }}
            title={
              <Box sx={{ py: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    display: "block",
                    color: theme.palette.text.primary,
                  }}
                >
                  {day.weekday} {day.date}
                  {day.isToday ? " · today" : ""}
                </Typography>
                {series.map((entry) => (
                  <Typography
                    key={entry.key}
                    variant="caption"
                    sx={{
                      display: "block",
                      fontSize: "0.7rem",
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {entry.label}: {Number(day[entry.key] || 0)}
                  </Typography>
                ))}
                {total === 0 ? (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      fontSize: "0.7rem",
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {emptyHint}
                  </Typography>
                ) : null}
              </Box>
            }
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
                cursor: "default",
              }}
            >
              {showValues ? (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: day.isToday ? 800 : 600,
                    color:
                      total === 0
                        ? theme.palette.text.secondary
                        : theme.palette.text.primary,
                    opacity: isDim ? 0.45 : 1,
                  }}
                >
                  {total}
                </Typography>
              ) : null}

              <Box
                sx={{
                  width: "100%",
                  height: barHeight,
                  display: "flex",
                  flexDirection: "column-reverse",
                  borderRadius: "4px",
                  overflow: "hidden",
                  bgcolor:
                    total === 0 ? theme.palette.action.hover : "transparent",
                  outline: day.isToday
                    ? `2px solid ${theme.palette.primary.main}`
                    : "none",
                  outlineOffset: 1,
                  opacity: isDim ? 0.45 : 1,
                  transition: "height 0.2s ease",
                }}
              >
                {series.map((entry) => {
                  const value = Number(day[entry.key] || 0);
                  if (value <= 0) return null;
                  return (
                    <Box
                      key={entry.key}
                      sx={{
                        width: "100%",
                        flex: value,
                        bgcolor: entry.color,
                      }}
                    />
                  );
                })}
              </Box>

              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: day.isToday ? 800 : 500,
                  color: day.isToday
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                  whiteSpace: "nowrap",
                }}
              >
                {day.weekday}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default WeekStrip;
